import { createStyles, Divider, Drawer, Stack } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { useEffect } from 'react';
import { useAuthUser } from '../../../api';
import type { AutomationJob } from '../../../api/types/models/automation/automationJob';
import { ANALYTICS_EVENTS } from '../../../utils/constants/analytics';
import { AutomationJobActionBar } from './AutomationJobActionBar';
import { AutomationJobActivity } from './AutomationJobActivity';
import { AutomationJobDrawerResourceTable } from './AutomationJobDrawerResourceTable';
import { AutomationJobDrawerSummary } from './AutomationJobDrawerSummary';

const useStyles = createStyles((theme) => ({
	drawerBody: {
		width: '100%',
		height: '100%',
		overflowY: 'auto',
		display: 'flex',
		padding: 0,
		gap: 0,
	},
	wrapper: {
		width: '100%',
		padding: 0,
		gap: 0,
	},
	content: {
		overflowY: 'auto',
		scrollbarGutter: 'stable',
		padding: `0 ${theme.spacing.xl}`,
		gap: theme.spacing['2xl'],
	},
}));

interface AutomationJobDrawerV2Props {
	automationJob: AutomationJob;
	opened: boolean;
	onClose: () => void;
	handleCompleteAutomationReview: (automationJobId: string) => void;
	isCompleteAutomationReviewLoading: boolean;
}

export function AutomationJobDrawerV2({
	automationJob,
	opened,
	onClose,
	handleCompleteAutomationReview,
	isCompleteAutomationReviewLoading,
}: AutomationJobDrawerV2Props) {
	const { classes, theme } = useStyles();
	const { user, workspace } = useAuthUser();

	// Track when review is started (drawer is opened for pending status)
	useEffect(() => {
		if (opened && automationJob.status === 'pending') {
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_REVIEW_STARTED,
				{
					automation_id: automationJob.automation?.toString() || '',
					job_id: automationJob.id,
					affected_records_count:
						automationJob.affected_records_count?.toString() || '0',
				},
				user,
				workspace
			);
		}
	}, [
		opened,
		automationJob.status,
		automationJob.automation,
		automationJob.id,
		automationJob.affected_records_count,
		user,
		workspace,
	]);

	// Track when job drawer is opened
	useEffect(() => {
		if (opened) {
			trackEvent(
				ANALYTICS_EVENTS.AI_OUTPUT_JOB_DRAWER_OPENED,
				{
					automation_id: automationJob.automation?.toString() || '',
					job_id: automationJob.id,
					job_status: automationJob.status || '',
				},
				user,
				workspace
			);
		}
	}, [
		opened,
		automationJob.automation,
		automationJob.id,
		automationJob.status,
		user,
		workspace,
	]);

	return (
		<Drawer
			classNames={{
				body: classes.drawerBody,
			}}
			size={theme.other.space[160]}
			withinPortal
			position="right"
			opened={opened}
			onClose={onClose}
			withCloseButton={false}
		>
			<Stack className={classes.wrapper}>
				<AutomationJobActionBar
					automationJob={automationJob}
					onClose={() => onClose()}
					handleCompleteAutomationReview={handleCompleteAutomationReview}
					isCompleteAutomationReviewLoading={isCompleteAutomationReviewLoading}
				/>
				<Stack className={classes.content}>
					<AutomationJobDrawerSummary automationJob={automationJob} />
					<Divider />
					<AutomationJobDrawerResourceTable
						automationJob={automationJob}
						handleCompleteAutomationReview={handleCompleteAutomationReview}
					/>
					<AutomationJobActivity automationJob={automationJob} />
				</Stack>
			</Stack>
		</Drawer>
	);
}
